// ✅ src/screens/MatchesScreen.js (FULL)
// Swipe-deck Matches screen (HomeScreen style)
// - Big image card
// - Overlay: Name + Age + Location + Relationship goal
// ✅ NOW: ❌ Pass + ❤️ Love + 💬 Chat
// ✅ Pass/Love/Chat are SAVED so those matches never show again (even after refresh)
// ✅ Removed "No matches yet" UI
// - Tap card -> opens public profile

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function calcAgeFromBirthDate(birthDateTs) {
  const ms = msFromTs(birthDateTs);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function makeChatId(a, b) {
  const x = safeStr(a);
  const y = safeStr(b);
  if (!x || !y) return "";
  return [x, y].sort().join("__");
}
function getOtherUidFromMatch(matchDoc, myUid) {
  const a = safeStr(matchDoc?.userA);
  const b = safeStr(matchDoc?.userB);
  if (a && a !== myUid) return a;
  if (b && b !== myUid) return b;
  return "";
}

/* ---------------- UI ---------------- */
function SwipeMatchCard({ person, swipeOut, onOpenProfile }) {
  const name = safeStr(person?.displayName) || "Unknown";
  const avatar = safeStr(person?.avatar) || safeStr(person?.photos?.[0]);
  const age = Number.isFinite(Number(person?.age)) ? Number(person.age) : null;
  const city = safeStr(person?.city);
  const goal = safeStr(person?.relationshipGoal);

  const animStyle =
    swipeOut === "right" ? S.cardSwipeRight : swipeOut === "left" ? S.cardSwipeLeft : null;

  return (
    <button
      type="button"
      style={S.bigCardBtn}
      onClick={onOpenProfile}
      aria-label={`Open ${name}'s profile`}
      title="View profile"
    >
      <div style={{ ...S.bigCard, ...(animStyle || null) }}>
        <div style={S.bigMedia}>
          {avatar ? (
            <img src={avatar} alt={name} style={S.bigImg} />
          ) : (
            <div style={S.bigFallback}>{initials(name)}</div>
          )}

          <div style={S.overlay}>
            <div style={S.nameRow}>
              <span style={S.name}>{name}</span>
              {age !== null ? <span style={S.age}>· {age}</span> : null}
            </div>

            {city ? <div style={S.metaLine}>📍 {city}</div> : null}
            {goal ? <div style={S.goalPill}>🔥 {goal}</div> : null}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ---------------- main ---------------- */
export default function MatchesScreen() {
  const nav = useNavigate();

  const [myUid, setMyUid] = useState("");
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);

  const [acting, setActing] = useState(false);
  const [swipeOut, setSwipeOut] = useState(""); // "" | "left" | "right"

  const current = deck[index] || null;

  const countLabel = useMemo(() => `${deck.length}`, [deck.length]);

  async function hydratePeople(ids) {
    const uniq = Array.from(new Set((ids || []).filter(Boolean))).slice(0, 240);
    const results = [];

    for (const uid of uniq) {
      try {
        const snap = await getDoc(doc(db, "datingProfiles", uid));
        if (!snap.exists()) {
          results.push({
            uid,
            displayName: "Unknown",
            avatar: "",
            photos: [],
            age: null,
            city: "",
            relationshipGoal: "",
          });
          continue;
        }
        const d = snap.data();
        const computed = calcAgeFromBirthDate(d?.birthDate);
        const age = Number.isFinite(Number(d?.age)) ? Number(d.age) : computed;

        results.push({
          uid,
          displayName: safeStr(d.displayName) || "Unknown",
          avatar: safeStr(d.avatar) || safeStr(d.photos?.[0]),
          photos: Array.isArray(d.photos) ? d.photos : [],
          age: Number.isFinite(Number(age)) ? Number(age) : null,
          city: safeStr(d.city),
          relationshipGoal: safeStr(d.relationshipGoal),
        });
      } catch {
        results.push({
          uid,
          displayName: "Unknown",
          avatar: "",
          photos: [],
          age: null,
          city: "",
          relationshipGoal: "",
        });
      }
    }

    return results;
  }

  // ✅ Save match swipe so it never comes back
  async function saveMatchSwipe({ action, otherUid }) {
    const u = auth.currentUser;
    const my = safeStr(u?.uid);
    const other = safeStr(otherUid);
    const a = safeStr(action);
    if (!my || !other || !a) return;

    try {
      await setDoc(
        doc(db, "datingProfiles", my, "matchSwipes", other),
        { action: a, createdAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error("[MATCHES] saveMatchSwipe error:", e);
    }
  }

  async function loadMatches(uid) {
    try {
      // ✅ read my match swipes (passed/liked/chatted) and exclude them
      const swipedSet = new Set();
      try {
        const swipeSnap = await getDocs(
          query(collection(db, "datingProfiles", uid, "matchSwipes"), limit(1200))
        );
        swipeSnap.forEach((d) => swipedSet.add(d.id));
      } catch {}

      const qA = getDocs(query(collection(db, "matches"), where("userA", "==", uid), limit(200)));
      const qB = getDocs(query(collection(db, "matches"), where("userB", "==", uid), limit(200)));
      const [snapA, snapB] = await Promise.all([qA, qB]);

      const map = new Map();
      snapA.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
      snapB.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));

      const matchDocs = Array.from(map.values());

      matchDocs.sort((a, b) => {
        const am = msFromTs(a.lastMessageAt) || msFromTs(a.createdAt) || 0;
        const bm = msFromTs(b.lastMessageAt) || msFromTs(b.createdAt) || 0;
        return bm - am;
      });

      // ✅ get other uids, filter out ones we already swiped in MatchesScreen
      const otherUids = matchDocs
        .map((m) => getOtherUidFromMatch(m, uid))
        .filter(Boolean)
        .filter((other) => !swipedSet.has(other));

      const people = await hydratePeople(otherUids);

      const withChat = people.map((p) => {
        const matchId =
          matchDocs.find((m) => getOtherUidFromMatch(m, uid) === p.uid)?.id ||
          makeChatId(uid, p.uid);
        return { ...p, chatId: matchId };
      });

      setDeck(withChat);
      setIndex(0);
    } catch (e) {
      console.error("[MATCHES] load error:", e);
      setDeck([]);
      setIndex(0);
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u?.uid) {
        nav("/login", { replace: true });
        return;
      }
      setMyUid(u.uid);
      loadMatches(u.uid);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPublicProfile = (uid) => {
    const id = safeStr(uid);
    if (!id) return;
    nav(`/public-profile/${id}`);
  };

  async function animateThenNext(dir, fn) {
    if (!current || acting) return;
    setActing(true);
    setSwipeOut(dir);

    try {
      await fn?.();
    } catch (e) {
      console.error("match action error", e);
    }

    setTimeout(() => {
      setIndex((i) => i + 1);
      setSwipeOut("");
      setActing(false);
    }, 260);
  }

  // ❌ Pass (left) => save + next
  async function onPass() {
    const c = current;
    if (!c?.uid) return;

    await animateThenNext("left", async () => {
      await saveMatchSwipe({ action: "pass", otherUid: c.uid });
    });
  }

  // ❤️ Love (right) => save + next
  async function onLove() {
    const c = current;
    if (!c?.uid) return;

    await animateThenNext("right", async () => {
      await saveMatchSwipe({ action: "like", otherUid: c.uid });
    });
  }

  // 💬 Chat => open chat + save + next
  async function onChat() {
    const c = current;
    if (!c?.uid) return;

    await animateThenNext("right", async () => {
      await saveMatchSwipe({ action: "chat", otherUid: c.uid });
      const chatId = safeStr(c.chatId) || makeChatId(myUid, c.uid);
      nav(`/chat/${chatId}`);
    });
  }

  return (
    <div style={S.stage}>
      <div style={S.headerRow}>
        <div style={S.headerLeft}>
          <div style={S.title}>Matches</div>
          <div style={S.subTitle}>({countLabel})</div>
        </div>

        <button type="button" style={S.refreshBtn} onClick={() => myUid && loadMatches(myUid)}>
          ♻️ Refresh
        </button>
      </div>

      {/* ✅ Removed "No matches yet" UI completely */}
      {!current ? null : (
        <>
          <SwipeMatchCard
            person={current}
            swipeOut={swipeOut}
            onOpenProfile={() => openPublicProfile(current.uid)}
          />

          <div style={S.actions}>
            <button
              type="button"
              style={{ ...S.actionBtn, ...S.noBtn }}
              onClick={onPass}
              disabled={acting}
              aria-label="Pass"
              title="Pass"
            >
              ❌
            </button>

            <button
              type="button"
              style={{ ...S.actionBtn, ...S.loveBtn }}
              onClick={onLove}
              disabled={acting}
              aria-label="Love"
              title="Love"
            >
              ❤️
            </button>

            <button
              type="button"
              style={S.chatBtn}
              onClick={onChat}
              disabled={acting}
              aria-label="Chat"
              title="Chat"
            >
              💬 Chat
            </button>
          </div>

          <div style={S.hint}>Tip: Tap the card to view profile • ❌ Pass • ❤️ Love • 💬 Chat opens chat</div>
        </>
      )}
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  stage: { display: "grid", gap: 14, justifyItems: "center", padding: 12 },

  headerRow: {
    width: "min(640px, 100%)",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  headerLeft: { display: "flex", gap: 8, alignItems: "baseline" },
  title: { fontWeight: 1000, fontSize: 18 },
  subTitle: { opacity: 0.7, fontWeight: 900, fontSize: 13 },

  refreshBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
  },

  bigCardBtn: {
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    width: "min(640px, 100%)",
  },
  bigCard: {
    width: "min(640px, 100%)",
    borderRadius: 28,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 22px 70px rgba(2,6,23,0.10)",
    overflow: "hidden",
    transition: "transform 240ms ease, opacity 240ms ease",
    willChange: "transform, opacity",
  },
  cardSwipeRight: { transform: "translateX(120%) rotate(10deg)", opacity: 0.0 },
  cardSwipeLeft: { transform: "translateX(-120%) rotate(-10deg)", opacity: 0.0 },

  bigMedia: { position: "relative" },
  bigImg: { width: "100%", height: 520, objectFit: "cover", display: "block" },
  bigFallback: {
    width: "100%",
    height: 520,
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.85)",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 44,
  },

  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 18,
    background:
      "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.45) 40%, rgba(0,0,0,.85) 100%)",
    color: "#fff",
  },
  nameRow: { display: "flex", alignItems: "baseline", gap: 8 },
  name: { fontWeight: 1000, fontSize: 24, letterSpacing: -0.5 },
  age: { fontWeight: 900, fontSize: 20, opacity: 0.85 },
  metaLine: { marginTop: 6, fontWeight: 900, fontSize: 14, opacity: 0.9 },

  goalPill: {
    marginTop: 10,
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 12,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.28)",
    backdropFilter: "blur(10px)",
  },

  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 2,
    flexWrap: "wrap",
  },

  actionBtn: {
    width: 68,
    height: 68,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    cursor: "pointer",
    fontSize: 28,
    fontWeight: 1000,
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.78)",
  },
  noBtn: { background: "rgba(255,255,255,0.80)" },
  loveBtn: {
    border: "none",
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    color: "#fff",
  },

  chatBtn: {
    height: 56,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    cursor: "pointer",
    fontWeight: 1000,
    background: "rgba(255,255,255,0.85)",
    boxShadow: "0 14px 34px rgba(2,6,23,0.10)",
  },

  hint: { fontWeight: 900, opacity: 0.65, fontSize: 13, textAlign: "center", paddingBottom: 6 },
};